/* File: src/components/game/hooks/useGameControls.js */
import { useEffect, useCallback } from 'react';
import { useGame } from '../../../contexts/GameContext'; // Hook to access game context
import { useSettings } from '../../../contexts/SettingsContext'; // Hook to access settings like keybindings

/**
 * Custom hook to manage game controls via keyboard input.
 * Handles:
 * - Pause/Resume toggle using Spacebar or 'P'.
 * - Visual/Audio match responses based on user-defined keybindings.
 * - Optional debug key combination.
 * Obtains necessary actions and refs directly from GameContext.
 */
const useGameControls = ({
    // === Dependencies passed from GameScreen ===
    // gameState is still useful passed down to control the useEffect listener attachment
    gameState,
    // Callbacks for match responses are passed from useGameResponses via GameScreen
    onPositionMatch,        // Callback function for visual match attempt
    onAudioMatch,           // Callback function for audio match attempt
    // Optional debug info
    debugInfo = null
}) => {
    // --- Context Access ---
    // Get necessary refs and actions directly from GameContext
    const {
        logReplayEvent,         // Function to log replay events
        latestGameStateRef,     // Ref to the latest game state
        currentTrialRef,        // Ref to the current trial index
        pauseGame,              // Action to pause the game
        resumeGame,             // Action to resume the game
        // endGame // Action to end the game (could be added if needed for a keybinding)
    } = useGame();
    // Get settings (specifically keyBindings) from SettingsContext
    const { settings } = useSettings();

    // --- Settings Values ---
    // Provide default keybindings if settings or keyBindings are not yet loaded/available
    const keyBindings = settings?.keyBindings || { visual: 'ArrowLeft', audio: 'ArrowRight' };

    // --- Combined Pause/Resume Handler (for Button & Keyboard) ---
    // Memoized function to toggle between playing and paused states.
    const handlePauseResume = useCallback(() => {
        const currentState = latestGameStateRef?.current; // Read latest state via ref
        if (currentState === 'playing') {
            console.log("[handlePauseResume] Pausing game...");
            if (logReplayEvent) logReplayEvent({ type: 'pause', timestamp: Date.now() });
            if (typeof pauseGame === 'function') {
                pauseGame();
            } else {
                console.error("[handlePauseResume] pauseGame action missing from GameContext!");
            }
        } else if (currentState === 'paused') {
            console.log("[handlePauseResume] Resuming game...");
            if (logReplayEvent) logReplayEvent({ type: 'resume', timestamp: Date.now() });
            if (typeof resumeGame === 'function') {
                resumeGame(); // resumeGame should handle audio context resumption
            } else {
                console.error("[handlePauseResume] resumeGame action missing from GameContext!");
            }
        } else {
            console.warn(`[handlePauseResume] Called in unexpected state: ${currentState}`);
        }
    }, [latestGameStateRef, pauseGame, resumeGame, logReplayEvent]); // Dependencies for the callback

    // --- Keyboard Event Handler (Memoized) ---
    // Handles all relevant keyboard inputs during the game.
    const handleKeyDown = useCallback((e) => {
        const currentState = latestGameStateRef?.current; // Read latest state for checks
        const target = e.target;
        // Ignore key presses if an input field, textarea, or editable element has focus
        const isInputFocused = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;

        // --- Pause/Resume Logic (Spacebar or 'P'/'p' key) ---
        if (!isInputFocused && (e.key === ' ' || e.key === 'p' || e.key === 'P')) {
            // Only allow toggle if game is currently playing or paused
            if (currentState === 'playing' || currentState === 'paused') {
                e.preventDefault(); // Prevent default spacebar/letter actions
                handlePauseResume(); // Use the combined handler
            }
        }
        // --- Debug Key (Ctrl+D or Cmd+D) ---
        else if (!isInputFocused && e.key === 'd' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            console.log("[handleKeyDown] Debug info requested:", debugInfo);
            // Add any other specific debug actions here if needed
        }
        // --- Game Response Keys (Visual/Audio Match) ---
        // Only process response keys if the game is actively 'playing'
        else if (currentState === 'playing' && !isInputFocused) {
            // Get key bindings from settings, providing defaults
            const visualKey = keyBindings.visual || 'ArrowLeft';
            const audioKey = keyBindings.audio || 'ArrowRight';
            // Get the current trial index *at the time of the key press* using the ref
            const currentTrialForResponse = currentTrialRef.current;

            // Check if the pressed key matches the visual binding
            if (e.key === visualKey) {
                // Ensure the handler function passed as a prop exists before calling
                if (typeof onPositionMatch === 'function') {
                    onPositionMatch(currentTrialForResponse); // Call the visual response handler
                } else {
                    console.error("[handleKeyDown] onPositionMatch handler prop is missing or not a function!");
                }
            }
            // Check if the pressed key matches the audio binding
            else if (e.key === audioKey) {
                 // Ensure the handler function passed as a prop exists before calling
                if (typeof onAudioMatch === 'function') {
                    onAudioMatch(currentTrialForResponse); // Call the audio response handler
                } else {
                     console.error("[handleKeyDown] onAudioMatch handler prop is missing or not a function!");
                }
            }
        }
    // Dependencies: Update handler if these functions, refs, or values change identity
    }, [
        latestGameStateRef, handlePauseResume, debugInfo, keyBindings,
        onPositionMatch, onAudioMatch, currentTrialRef // Use the ref from context, callbacks from props
    ]);

    // --- Effect: Attach/Detach Keyboard Listener ---
    // Manages adding and removing the global keydown listener based on game state.
    useEffect(() => {
        // Only listen for keys when the game is actively playing or paused
        if (gameState === 'playing' || gameState === 'paused') {
            // console.log(`[useGameControls Attach] Attaching keydown listener (State: ${gameState})`);
            window.addEventListener('keydown', handleKeyDown);
            // Cleanup function removes listener when effect dependencies change or component unmounts
            return () => {
                // console.log(`[useGameControls Detach] Detaching keydown listener`);
                window.removeEventListener('keydown', handleKeyDown);
            };
        }
        // If state is not playing/paused, ensure no listener is attached (return undefined)
        return undefined;
    // Dependencies: Re-attach/detach listener if game state or the handler function changes
    }, [gameState, handleKeyDown]);


    // --- Return Values ---
    // Return the memoized handlers needed by the parent component (GameScreen) or its children.
    // `handleInteractionStart` is usually handled directly in GameScreen.
    return {
        handlePauseResume   // Expose Pause/Resume handler for UI buttons (e.g., in GameHeader)
        // handleKeyDown is managed internally by the effect, no need to return usually
    };
};

export default useGameControls; // Ensure it's the default export